import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { getTranslatedString } from 'helpers/i18n';
import { tenantResidentVerbiage } from 'helpers/StringHelper';
import { status as ByesignStatuses } from 'components/byesign/ByesignHelper';
import useInterval from 'hooks/useInterval';
import capitalize from 'capitalize';
import Stepper from 'components/ui/Stepper/Stepper';

import {
  openLeaseInPersonOverwriteCreator,
  remindRenewalSignatures,
} from 'actions/landlordApplicationsActions';
import { openEditLease } from 'actions/LeaseDetailsActions';
import { submitRenewal } from '../renewals/store/actions';
import { showFailure, showSuccess } from 'actions/notificationActions';

const LEASE_POLLING_INTERVAL_IN_MS = 3000;

const steps = [
  'review_renewal',
  'pending_tenant_applications',
  'pending_send_renewal',
  'pending_tenant_signature',
  'pending_landlord_signature',
];

const YardiRenewalStepper = ({
  leaseDetails,
  remindRenewalSignatures,
  openLeaseInPersonOverwriteCreator,
  propertyTypes,
  openEditLease,
  submitRenewal,
  showSuccess,
  showFailure,
  setInPersonDocumentSigning,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [pollingLease, setPollingLease] = useState(false);
  const [pollingNoticeOfIncreaseDocuments, setPollingNoticeOfIncreaseDocuments] = useState(false);
  const [isFetchingLease, setIsFetchingLease] = useState(false);
  const [sendRenewalLoadingState, setSendRenewalLoadingState] = useState(false);

  const tenantResident = tenantResidentVerbiage(propertyTypes);

  const getRenewalStep = useCallback(
    renewalStatus => {
      let originalSteps = [...steps];

      if (leaseDetails.has_notice_of_increase_documents) {
        // insert the pending notice step before pending_tenant_signature
        originalSteps.splice(3, 0, 'pending_notice_of_increase_signatures');
      }

      let stepIndex = originalSteps.findIndex(step => {
        return step === renewalStatus;
      });
      if (stepIndex === -1) {
        // If we can't find the status, assume they are all complete
        // return the last index of steps
        stepIndex = originalSteps.length - 1;
      }
      return stepIndex;
    },
    [leaseDetails.has_notice_of_increase_documents]
  );

  useEffect(
    () => {
      const renewalStep = getRenewalStep(leaseDetails.renewal_status);
      setCurrentStep(renewalStep);
    },
    [leaseDetails, getRenewalStep]
  );

  const shouldStartPollingLeaseDetails = useCallback(
    () => {
      return (
        leaseDetails.document_status === ByesignStatuses.CREATING_DOCUMENT &&
        currentStep === getRenewalStep('pending_tenant_signature')
      );
    },
    [leaseDetails.document_status, currentStep, getRenewalStep]
  );

  const shouldStopPollingNoticeOfIncreaseDocuments = leaseDetails => {
    const {
      document_status,
      renewal_status,
      notice_of_increase_document_status,
      certificate_of_service_document_status,
      has_notice_of_increase_documents,
    } = leaseDetails;

    // Stop if either:
    // both notice of increase and certificate of service documents are created
    // or the lease is created and we're awaiting tenant signature
    return (
      has_notice_of_increase_documents &&
      ((notice_of_increase_document_status === ByesignStatuses.DOCUMENT_CREATED &&
        certificate_of_service_document_status === ByesignStatuses.DOCUMENT_CREATED) ||
        ([ByesignStatuses.DOCUMENT_CREATED, ByesignStatuses.PENDING_SIGNATURES].includes(
          document_status
        ) &&
          renewal_status === 'pending_tenant_signature') ||
        (document_status === ByesignStatuses.AGENT_SIGNATURE_REQUIRED &&
          renewal_status === 'pending_landlord_signature'))
    );
  };

  const shouldStartPollingNoticeOfIncreaseDocuments = useCallback(
    () => {
      const {
        has_notice_of_increase_documents,
        notice_of_increase_document_status,
        certificate_of_service_document_status,
      } = leaseDetails;

      // start polling (and show loading) if either the notice of increase or certificate of service document are still being made
      // or if both documents are signed (we're waiting for the signature callback to update the renewal status to the next step)
      return (
        currentStep === getRenewalStep('pending_notice_of_increase_signatures') &&
        has_notice_of_increase_documents &&
        (notice_of_increase_document_status === ByesignStatuses.CREATING_DOCUMENT ||
          certificate_of_service_document_status === ByesignStatuses.CREATING_DOCUMENT ||
          (notice_of_increase_document_status === ByesignStatuses.DOCUMENT_COMPLETED &&
            certificate_of_service_document_status === ByesignStatuses.DOCUMENT_COMPLETED))
      );
    },
    [currentStep, getRenewalStep, leaseDetails]
  );

  useEffect(
    () => {
      if (shouldStartPollingLeaseDetails()) {
        setPollingLease(true);
      }
    },
    [shouldStartPollingLeaseDetails]
  );

  useEffect(
    () => {
      if (shouldStartPollingNoticeOfIncreaseDocuments()) {
        setPollingNoticeOfIncreaseDocuments(true);
      }
    },
    [setPollingNoticeOfIncreaseDocuments, shouldStartPollingNoticeOfIncreaseDocuments]
  );

  const fetchLeaseDetails = async leaseId => {
    return await openEditLease(leaseId);
  };

  useInterval(async () => {
    if (isFetchingLease) return null;

    setIsFetchingLease(true);
    const res = await fetchLeaseDetails(leaseDetails.id);
    setIsFetchingLease(false);

    const { document_status, renewal_status } = res.value.lease_details;
    if (
      renewal_status === 'pending_tenant_signature' &&
      document_status === ByesignStatuses.DOCUMENT_CREATED
    ) {
      setPollingLease(false);
    }
  }, pollingLease ? LEASE_POLLING_INTERVAL_IN_MS : null);

  useInterval(async () => {
    if (isFetchingLease) return null;

    setIsFetchingLease(true);
    const res = await fetchLeaseDetails(leaseDetails.id);
    setIsFetchingLease(false);

    if (shouldStopPollingNoticeOfIncreaseDocuments(res.value.lease_details)) {
      setPollingNoticeOfIncreaseDocuments(false);
    }
  }, pollingNoticeOfIncreaseDocuments ? LEASE_POLLING_INTERVAL_IN_MS : null);

  const sendRenewal = async () => {
    setSendRenewalLoadingState(true);
    try {
      await submitRenewal(leaseDetails.renewal_id);
      showSuccess(getTranslatedString('landlordPortal.rentRoll.renewals.renewalSent'));
      fetchLeaseDetails(leaseDetails.id);
    } catch {
      showFailure(getTranslatedString('landlordPortal.rentRoll.renewals.renewalFailedToSend'));
    } finally {
      setSendRenewalLoadingState(false);
    }
  };

  const signInPerson = () => {
    openLeaseInPersonOverwriteCreator(
      leaseDetails.document_uuid,
      leaseDetails.signer_uuid,
      'LEASE_RENEWAL'
    );
  };

  const sendRenewalSignatureReminderEmail = async () => {
    await remindRenewalSignatures(leaseDetails.id);
    fetchLeaseDetails(leaseDetails.id);
  };

  let stepperSteps = [
    {
      title: getTranslatedString('landlordPortal.rentRoll.renewals.reviewRenewal'),
    },
    {
      title: getTranslatedString('landlordPortal.rentRoll.renewals.pendingApplication'),
    },
    {
      title: getTranslatedString('landlordPortal.rentRoll.renewals.sendRenewal'),
      description: getTranslatedString('landlordPortal.rentRoll.renewals.sendRenewalDetails'),
      primaryActionText: getTranslatedString('landlordPortal.rentRoll.renewals.sendRenewal'),
      primaryActionIsLoading: sendRenewalLoadingState,
      primaryAction: sendRenewal,
    },
    {
      title: getTranslatedString('landlordPortal.rentRoll.renewals.pendingTenantSignature', {
        tenantResident: capitalize(tenantResident),
      }),
      description: getTranslatedString(
        'landlordPortal.rentRoll.renewals.pendingTenantSignatureDetails',
        {
          tenantResident: tenantResident,
        }
      ),
      primaryActionText: getTranslatedString('landlordPortal.rentRoll.renewals.remindLeaseHolders'),
      primaryAction: sendRenewalSignatureReminderEmail,
      secondaryActionText: getTranslatedString('landlordPortal.rentRoll.renewals.signInPerson'),
      secondaryAction: signInPerson,
      isLoading: pollingLease,
      loadingDescription: getTranslatedString(
        'landlordPortal.rentRoll.renewals.documentLoadingDescription'
      ),
    },
    {
      title: getTranslatedString('landlordPortal.rentRoll.renewals.pendingLandlordSignature'),
      primaryActionText: getTranslatedString('landlordPortal.rentRoll.renewals.signRenewal'),
      primaryAction: signInPerson,
    },
  ];

  if (leaseDetails.has_notice_of_increase_documents) {
    let loadingDescription = getTranslatedString(
      'landlordPortal.rentRoll.renewals.pleaseWaitNoticeOfIncrease'
    );
    if (
      leaseDetails.certificate_of_service_document_status === ByesignStatuses.DOCUMENT_COMPLETED &&
      leaseDetails.notice_of_increase_document_status === ByesignStatuses.DOCUMENT_COMPLETED
    ) {
      loadingDescription = getTranslatedString('landlordPortal.rentRoll.renewals.sendingDocument');
    }
    // insert the pending notice step before pending_tenant_signature
    stepperSteps.splice(3, 0, {
      title: getTranslatedString('landlordPortal.rentRoll.renewals.signNoticeOfIncrease'),
      description: getTranslatedString(
        'landlordPortal.rentRoll.renewals.clickBelowNoticeOfIncrease'
      ),
      isLoading: pollingNoticeOfIncreaseDocuments,
      loadingDescription: loadingDescription,
      primaryActionText: getTranslatedString(
        'landlordPortal.rentRoll.renewals.ctaNoticeOfIncrease'
      ),
      primaryAction: () => {
        setInPersonDocumentSigning(true);
      },
    });
  }

  return <Stepper currentStep={currentStep} steps={stepperSteps} />;
};

const mapStateToProps = store => ({
  application: store.landlordApplications.application,
  leaseDetails: store.leaseDetails.leaseDetails,
  propertyTypes: store.userTypes.propertyTypes,
});

const mapDispatchToProps = {
  openLeaseInPersonOverwriteCreator,
  remindRenewalSignatures,
  openEditLease,
  submitRenewal,
  showSuccess,
  showFailure,
};

export default connect(mapStateToProps, mapDispatchToProps)(YardiRenewalStepper);
